import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { contactInfo } from "@/mocks/site";
import { usePageMeta } from "@/hooks/usePageMeta";

const submitAddr = "https://readdy.ai/api/form/db9mutrk13ra8mig95vg";

type Status = "idle" | "submitting" | "success" | "error";

export default function About() {
  usePageMeta(
    "关于我们 - 色戒未删减版",
    "了解色戒未删减版资料站的定位、内容原则与联系方式。本站专注《色戒》完整版介绍、剧情解析与影评整理，所有内容为虚构演示。",
    "关于色戒未删减版,影视资料站,联系方式,版权声明,使用说明",
  );

  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim()) {
        payload.append(key, value.trim());
      }
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(submitAddr, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMessage =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam = String(serverMessage).toLowerCase().includes("spam");
      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(
          String(serverMessage) || "提交没有成功，请稍后重试或直接发送邮件联系我们。",
        );
        return;
      }
      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络连接异常，请检查网络后重试，或直接发送邮件联系我们。");
    }
  };

  return (
    <SiteLayout>
      <header className="relative overflow-hidden border-b border-background-800/70 pt-[72px]">
        <img
          src="https://readdy.ai/api/search-image?query=Quiet%20archive%20reading%20room%20with%20tall%20wooden%20bookshelves%2C%20stacked%20film%20canisters%20and%20old%20newspapers%20on%20a%20long%20table%2C%20brass%20desk%20lamps%20casting%20warm%20amber%20light%2C%20deep%20crimson%20curtains%2C%20dust%20in%20the%20air%2C%20atmospheric%20editorial%20photography%2C%20muted%20rich%20tones%2C%20no%20text&width=1920&height=640&seq=sejie-about-header-01&orientation=landscape"
          alt="影视资料档案室氛围背景"
          title="关于色戒未删减版 - 资料档案"
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/90 to-background-950/40" />
        <div className="relative z-10 mx-auto max-w-[1180px] px-4 pb-14 pt-12 md:px-8 md:pb-16 md:pt-16">
          <nav className="flex flex-wrap items-center gap-2 text-[12px] text-foreground-500">
            <Link to="/" className="transition-colors hover:text-accent-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-600" />
            <span className="text-foreground-300">关于我们</span>
          </nav>
          <p className="mt-6 font-label text-[10px] uppercase tracking-[0.28em] text-accent-400">
            About The Archive
          </p>
          <h1 className="mt-4 max-w-3xl font-heading text-[30px] leading-[1.25] text-foreground-50 md:text-[44px]">
            一个由影迷维护的电影资料档案
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-[1.95] text-foreground-300">
            我们相信，理解一部电影需要的不只是热情，还需要可以查证的信息、可以复核的镜头，以及愿意慢下来的耐心。
          </p>
        </div>
      </header>

      <section className="bg-background-950 py-14 md:py-20">
        <div className="mx-auto max-w-[1180px] px-4 md:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
            <div>
              <h2 className="font-heading text-2xl text-foreground-50 md:text-[30px]">
                网站介绍
              </h2>
              <div className="mt-5 space-y-4 text-[14.5px] leading-[2.05] text-foreground-400">
                <p>
                  「色戒未删减版」是一个专注电影《色戒》的影视资料站。我们把公开可查的版本信息、镜头分析与幕后访谈整理成结构化的文章，帮助影迷在观影前后建立完整的认知。
                </p>
                <p>
                  站内内容分为四类：完整版介绍、影评、拍摄花絮与剧情解析。每篇文章都标注了发布时间与阅读时长，专题页则按章节提供目录联动，方便反复查阅。
                </p>
                <p>
                  我们不追逐热度，也不提供未经核实的小道消息。所有涉及剧情与镜头的讨论都尽量落回画面本身，让观点可以被讨论、被检验。
                </p>
              </div>

              <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {[
                  {
                    icon: "ri-shield-check-line",
                    title: "资料优先",
                    body: "先确认版本与口径，再谈差异与感受。",
                  },
                  {
                    icon: "ri-eye-line",
                    title: "回到画面",
                    body: "以镜头、光线、声音为讨论依据。",
                  },
                  {
                    icon: "ri-heart-3-line",
                    title: "尊重创作",
                    body: "只做评论与资料整理，不提供片源。",
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="rounded-lg border border-background-800/80 bg-background-900/40 p-4"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-full border border-accent-500/40 text-accent-300">
                      <i className={`${item.icon} text-base`} />
                    </span>
                    <h3 className="mt-3 font-heading text-[14px] text-foreground-50">
                      {item.title}
                    </h3>
                    <p className="mt-1.5 text-[12px] leading-relaxed text-foreground-500">
                      {item.body}
                    </p>
                  </div>
                ))}
              </div>

              <div id="usage" className="mt-10 scroll-mt-[100px]">
                <h2 className="font-heading text-xl text-foreground-50 md:text-2xl">
                  使用说明
                </h2>
                <ul className="mt-4 space-y-3 text-[13.5px] leading-[1.95] text-foreground-400">
                  <li className="flex gap-3">
                    <i className="ri-checkbox-circle-line mt-0.5 text-accent-500" />
                    顶部搜索支持按标题、摘要与标签查找内容；分类页可叠加类型、时间与热度筛选。
                  </li>
                  <li className="flex gap-3">
                    <i className="ri-checkbox-circle-line mt-0.5 text-accent-500" />
                    详情页的收藏按钮会把文章保存在浏览器本地，不会上传到任何服务器。
                  </li>
                  <li className="flex gap-3">
                    <i className="ri-checkbox-circle-line mt-0.5 text-accent-500" />
                    专题页目录会随滚动自动高亮，也可以点击章节直接跳转。
                  </li>
                </ul>
              </div>

              <div id="copyright" className="mt-10 scroll-mt-[100px]">
                <h2 className="font-heading text-xl text-foreground-50 md:text-2xl">
                  版权声明
                </h2>
                <p className="mt-4 rounded-lg border border-background-800/80 bg-background-900/40 p-4 text-[13px] leading-[1.95] text-foreground-500">
                  本站为影视资料演示站点，全部文章、数据与图片均为虚构示例，仅用于展示内容结构与阅读体验，不构成对任何影片的发行、播放或下载服务。如涉及真实作品信息，请以官方发布为准。
                </p>
              </div>
            </div>

            <div id="contact" className="scroll-mt-[100px]">
              <div className="rounded-lg border border-background-800/80 bg-background-900/50 p-5 md:p-7 lg:sticky lg:top-[100px]">
                <h2 className="font-heading text-xl text-foreground-50 md:text-2xl">
                  联系我们
                </h2>
                <p className="mt-2 text-[13px] leading-relaxed text-foreground-500">
                  资料勘误、内容建议或合作咨询，都可以通过下面的表单告诉我们。
                </p>

                <div className="mt-5 space-y-2.5 border-b border-background-800/80 pb-5 text-[12.5px] text-foreground-400">
                  <p className="flex items-center gap-2">
                    <i className="ri-mail-line text-accent-400" />
                    {contactInfo.email}
                  </p>
                  <p className="flex items-center gap-2">
                    <i className="ri-time-line text-accent-400" />
                    {contactInfo.hours}
                  </p>
                </div>

                <form
                  id="sejie-contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="mt-5 space-y-4"
                >
                  <div>
                    <label
                      htmlFor="contact-name"
                      className="mb-1.5 block text-[12px] text-foreground-400"
                    >
                      称呼
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      maxLength={40}
                      placeholder="怎么称呼你"
                      className="h-11 w-full rounded-md border border-background-700 bg-background-950 px-3 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-accent-600/70 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contact-email"
                      className="mb-1.5 block text-[12px] text-foreground-400"
                    >
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      maxLength={80}
                      placeholder="用于接收回复"
                      className="h-11 w-full rounded-md border border-background-700 bg-background-950 px-3 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-accent-600/70 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contact-topic"
                      className="mb-1.5 block text-[12px] text-foreground-400"
                    >
                      主题
                    </label>
                    <select
                      id="contact-topic"
                      name="topic"
                      defaultValue="内容建议"
                      className="h-11 w-full rounded-md border border-background-700 bg-background-950 px-3 text-sm text-foreground-100 focus:border-accent-600/70 focus:outline-none"
                    >
                      <option value="内容建议">内容建议</option>
                      <option value="资料勘误">资料勘误</option>
                      <option value="合作咨询">合作咨询</option>
                      <option value="其他问题">其他问题</option>
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="contact-message"
                      className="mb-1.5 block text-[12px] text-foreground-400"
                    >
                      留言内容
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="最多 500 字"
                      className="w-full resize-none rounded-md border border-background-700 bg-background-950 px-3 py-2.5 text-sm leading-relaxed text-foreground-100 placeholder:text-foreground-600 focus:border-accent-600/70 focus:outline-none"
                    />
                  </div>

                  <div className="form-aux" aria-hidden="true">
                    <label htmlFor="contact-website-alt">Website</label>
                    <input
                      id="contact-website-alt"
                      name="website_alt"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      readOnly
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex h-11 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm text-foreground-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
                    {status === "submitting" ? "正在提交..." : "提交留言"}
                  </button>

                  {status === "success" ? (
                    <p className="flex items-start gap-2 rounded-md border border-accent-500/40 bg-accent-500/10 px-3 py-2.5 text-[12px] leading-relaxed text-accent-200">
                      <i className="ri-checkbox-circle-line mt-0.5" />
                      留言已收到，感谢你的反馈。我们会尽快查看。
                    </p>
                  ) : null}
                  {status === "error" ? (
                    <p className="flex items-start gap-2 rounded-md border border-primary-500/50 bg-primary-500/10 px-3 py-2.5 text-[12px] leading-relaxed text-primary-200">
                      <i className="ri-error-warning-line mt-0.5" />
                      {formError || "提交没有成功，请稍后重试。"}
                    </p>
                  ) : null}

                  <p className="text-[11px] leading-relaxed text-foreground-600">
                    提交即表示你同意我们仅将该信息用于回复本次咨询。本站不收集与业务无关的个人信息。
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}